/**
 * B397 leg (4) — the confirmation @390 as ONE screen
 * (redline-storefront-confirmation-390.md, 74:2): the centerd Done card
 * (success badge · You’re booked · the one-sentence summary · the code
 * chip · the QR · the gate sentence), the slim tickets card, and the
 * sticky save door. This face is the buyer's ticket home — everything on
 * it is evidence they keep.
 *
 * Honesty seams, each from a read or absent (§10):
 *  - the summary sentence is derived (lib/confirmation.ts
 *    confirmationSummary) from the lines, catalog names and captured slot
 *    labels — a missing fact drops its segment, never a placeholder;
 *  - the QR tiles render only from loaded ttk1 tokens (the same bytes the
 *    validator scans, §8.1); an unloaded token keeps the preparing face;
 *  - the gate sentence's email clause and the tickets card's sent-to
 *    footer render ONLY when checkout carried a buyer email, and say
 *    on-its-way because no read carries a sent receipt;
 *  - the sticky door is the print/save-as-PDF door labeled for what it
 *    does — no wallet integration exists, and a wallet button that opens
 *    nothing is a dead door (74:2's own instruction);
 *  - rows say "Ticket N": no read joins an entitlement to its ticket
 *    TYPE (entitlement.list carries id + state only) — the type column is
 *    a priced read amendment on the item line, not a guess.
 *
 * Migrated walls: B68(b) raw tokens behind Advanced only, QR named for
 * AT; B115 the code leads with quote-this-code guidance; B205 the order
 * id demotes to the fine-print record line but SHOWS (buyer evidence);
 * B306 the package ConfirmationCode chip owns the copy affordance;
 * B115(b) REPOINTED: the per-row code stamp retired with the card layout
 * — the printed page still names the booking because the Done card
 * (code chip and all) prints above the rows in the same sheet.
 */
import type { ReactElement } from "react";
import { Button, ConfirmationCode, Icon, SheetCard, StickyBar } from "@transact/ui";
import { formatMoney, type Money } from "../lib/money.js";
import { buyerTicketStatus, gateSentence } from "../lib/confirmation.js";
import type { ChargeRow } from "../lib/cartPayFace.js";

/** B68(b): one view-model row per ticket — QR injected so the markup stays pure. */
export interface TicketView {
  readonly entitlementId: string;
  readonly state: string;
  /** Signed ttk1 token (B20b(e)); renders ONLY behind the Advanced disclosure. */
  readonly token?: string;
  /** The token as a rendered QR image (lib/qr.ts); absent while it loads. */
  readonly qrDataUrl?: string;
}

/** 74:2's dot: green is VALID's alone; a refund never wears it. */
function rowDotClass(word: string | undefined): string {
  if (word === "Valid") return "confirm-row-dot confirm-row-dot--valid";
  if (word === "Canceled") return "confirm-row-dot confirm-row-dot--danger";
  return "confirm-row-dot";
}

/**
 * B629 (c) — ", including Service fee $4.00 and Tax $10.30": the record
 * line keeps the breakdown the buyer read on Cart and Pay. Nothing charged
 * beyond the lines = nothing said.
 */
function includingWords(charges: readonly ChargeRow[]): string {
  const said = charges.map((c) => `${c.label} ${formatMoney(c.amount)}`);
  if (said.length === 0) return "";
  const last = said.pop() as string;
  return `, including ${said.length === 0 ? last : `${said.join(", ")} and ${last}`}`;
}

export function ConfirmationView(props: {
  readonly orderId: string;
  readonly tickets: readonly TicketView[];
  /** The frame's one sentence (74:2): product · day-and-time · party. */
  readonly summarySentence?: string;
  readonly total?: Money;
  /** B629 (c) — what the total paid includes (each fee, then tax), off the confirmed order's own read. */
  readonly charges?: readonly ChargeRow[];
  readonly promoApplied?: boolean;
  /** B115 — the human confirmation code; absent on pre-B115 orders and unwired stages. */
  readonly confirmationCode?: string;
  /** The email checkout carried; gates the email clauses (§10). */
  readonly buyerEmail?: string;
  /** The save/print door; absent handler, no door — never a dead button. */
  readonly onSave?: () => void;
}): ReactElement {
  const n = props.tickets.length;
  const code = props.confirmationCode;
  const loaded = props.tickets.filter((t) => t.qrDataUrl !== undefined);
  const gate = gateSentence(n, props.buyerEmail !== undefined);
  return (
    <div className="confirmation confirm">
      <SheetCard redline="confirm-done" className="confirm-done">
        <span className="confirm-badge" data-redline="confirm-badge" aria-hidden="true">
          {/* the package's own check glyph (UI law) — the circle is this
             page's 74:2 recipe, the vector is the fleet's */}
          <Icon name="check" size={26} />
        </span>
        <h2 className="confirm-headline" data-redline="confirm-headline">
          You’re booked
        </h2>
        {props.summarySentence !== undefined ? (
          <p className="confirm-summary" data-redline="confirm-summary">
            {props.summarySentence}
          </p>
        ) : null}
        {code !== undefined ? (
          /* B115: the code the buyer will quote — leads, on the package chip (B306). */
          <section className="confirmation-code confirm-code" aria-label="Confirmation code" data-redline="confirm-code">
            <p className="code-label">Confirmation code</p>
            <p className="code-value">
              <ConfirmationCode code={code} />
            </p>
            <p className="code-hint">Quote this code at the counter or whenever you contact us about this booking.</p>
          </section>
        ) : null}
        {loaded.length > 0 ? (
          <div className="confirm-qr-rail" data-redline="confirm-qr">
            {loaded.map((t) => {
              const at = props.tickets.indexOf(t) + 1;
              return (
                <span key={t.entitlementId} className="confirm-qr-tile">
                  <img src={t.qrDataUrl} alt={`Ticket ${at} QR code — show this at entry`} />
                  {n > 1 ? <span className="confirm-qr-caption">Ticket {at}</span> : null}
                </span>
              );
            })}
          </div>
        ) : n > 0 ? (
          <p className="ticket-pending">
            {n === 1 ? "Preparing your ticket… it will appear here in a moment." : "Preparing your tickets… they will appear here in a moment."}
          </p>
        ) : null}
        {gate !== undefined ? (
          <p className="confirm-gate" data-redline="confirm-gate">
            {gate}
          </p>
        ) : null}
        {/* B205 — the buyer's own evidence stays shown; it demotes, never hides. */}
        <p className="confirm-record" data-redline="confirm-record">
          Order <code>{props.orderId}</code>
          {props.total !== undefined ? ` · Total paid ${formatMoney(props.total)}${includingWords(props.charges ?? [])}` : ""}
          {props.promoApplied === true ? " · Promo applied" : ""}
        </p>
      </SheetCard>

      {n > 0 ? (
        <SheetCard redline="confirm-tickets" className="confirm-tickets">
          <ul className="confirm-rows">
            {props.tickets.map((t, i) => {
              const word = buyerTicketStatus(t.state);
              return (
                <li key={t.entitlementId} className="confirm-row">
                  <span className={rowDotClass(word)} aria-hidden="true" />
                  <span className="confirm-row-name">Ticket {i + 1}</span>
                  {word !== undefined ? <span className="confirm-row-state">{word}</span> : null}
                  {t.token !== undefined ? (
                    /* B68(b)/B20b(e): the raw token exists for support cases ONLY. */
                    <details className="ticket-advanced">
                      <summary>Advanced</summary>
                      <code>{t.token}</code>
                    </details>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {props.buyerEmail !== undefined ? (
            /* No reply-handling read exists and no tenant contact read exists —
               the honest footer is the fact alone (74:2's own else-branch). */
            <p className="confirm-sent" data-redline="confirm-sent">
              Sent to {props.buyerEmail}
            </p>
          ) : null}
        </SheetCard>
      ) : null}

      {props.onSave !== undefined && n > 0 ? (
        <StickyBar redline="confirm-sticky">
          {/* B403 (74:2, the copy wall): the frame's words — the door still
              opens the browser's save/print sheet, which IS how a phone
              saves them; reversal is one line. */}
          <Button size="lg" onClick={props.onSave}>
            Save tickets to your phone
          </Button>
        </StickyBar>
      ) : null}
    </div>
  );
}
